import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Menu, LogOut, Settings } from 'lucide-react';
import NavLink from './NavLink';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useLanguage } from '@/hooks/useLanguage';
import { useTranslation } from '@/lib/i18n';
import { Button } from '@/components/ui/button';

import { 
  Sheet, 
  SheetContent, 
  SheetTrigger,
  SheetClose
} from "@/components/ui/sheet";

interface MobileNavigationProps {
  isHomePage: boolean;
  isScrolled: boolean;
  user: any;
  member: any;
  isAdmin: boolean;
  isMember: boolean;
  logout: () => void;
}

const MobileNavigation: React.FC<MobileNavigationProps> = React.memo(({ isHomePage, isScrolled, user, member, isAdmin, isMember, logout }) => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const navigate = useNavigate();

  const handleJoin = () => {
    navigate('/member-access');
  };

  const handleLogout = () => {
    logout();
  };
  
  return (
    <div>
      <Sheet>
        <SheetTrigger asChild>
          <button
            className="md:hidden text-white"
          >
            <Menu size={24} />
          </button>
        </SheetTrigger>
        <SheetContent side="left" className="w-full p-0 max-w-xs">
          <div className="p-6">
            <div className="flex items-center justify-between mb-8">
              <Link to="/" className="font-bold text-2xl text-node-copper">Singularité</Link>
              <LanguageSwitcher />
            </div>
            
            <div className="flex flex-col space-y-6">
              {!isMember && !isAdmin && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-white">{t('visionMission')}</h3>
                  <div className="ml-4 flex flex-col space-y-3">
                    {isHomePage ? (
                      <>
                        <SheetClose asChild>
                          <NavLink href="#vision" mobile>{t('vision')}</NavLink>
                        </SheetClose>
                        <SheetClose asChild>
                          <NavLink href="#benefits" mobile>{t('benefits')}</NavLink>
                        </SheetClose>
                        <SheetClose asChild>
                          <NavLink href="#who" mobile>{t('targetAudience')}</NavLink>
                        </SheetClose>
                        <SheetClose asChild>
                          <NavLink href="#testimonials" mobile>{t('testimonials')}</NavLink>
                        </SheetClose>
                      </>
                    ) : (
                      <>
                        <SheetClose asChild>
                          <NavLink to="/#vision" isPage mobile>{t('vision')}</NavLink>
                        </SheetClose>
                        <SheetClose asChild>
                          <NavLink to="/#benefits" isPage mobile>{t('benefits')}</NavLink>
                        </SheetClose>
                        <SheetClose asChild>
                          <NavLink to="/#who" isPage mobile>{t('targetAudience')}</NavLink>
                        </SheetClose>
                        <SheetClose asChild>
                          <NavLink to="/#testimonials" isPage mobile>{t('testimonials')}</NavLink>
                        </SheetClose>
                      </>
                    )}
                  </div>
                </div>
              )}
              
              <div className="flex flex-col space-y-3">
                <SheetClose asChild>
                  <NavLink to="/members" isPage mobile>Annuaire</NavLink>
                </SheetClose>
                <SheetClose asChild>
                  <NavLink to="/networking" isPage mobile>Networking</NavLink>
                </SheetClose>
                <SheetClose asChild>
                  <NavLink to="/opportunities" isPage mobile>Opportunités</NavLink>
                </SheetClose>
                <SheetClose asChild>
                  <NavLink to="/events" isPage mobile>Événements</NavLink>
                </SheetClose>
                <SheetClose asChild>
                  <NavLink to="/insights" isPage mobile>Insights</NavLink>
                </SheetClose>
                <SheetClose asChild>
                  <NavLink to="/workshops" isPage mobile>Workshops</NavLink>
                </SheetClose>
                
                {isMember && (
                  <SheetClose asChild>
                    <NavLink to="/dashboard" isPage mobile>Mon Espace</NavLink>
                  </SheetClose>
                )}

                {isAdmin && (
                  <SheetClose asChild>
                    <NavLink 
                      to="/admin" 
                      isPage 
                      mobile 
                      className="flex items-center gap-2 text-node-copper hover:underline"
                    >
                      <Settings className="h-4 w-4" />
                      Administration
                    </NavLink>
                  </SheetClose>
                )}
              </div>
            </div>

            <div className="flex flex-col space-y-3 mt-4">
              {!user || !member ? (
                <SheetClose asChild>
                  <Button 
                    onClick={handleJoin}
                    className="bg-node-copper hover:bg-node-copper/90 text-white w-full"
                  >
                    {t('join')}
                  </Button>
                </SheetClose>
              ) : (
                <SheetClose asChild>
                  <Button 
                    onClick={handleLogout}
                    variant="destructive"
                    className="bg-red-500 hover:bg-red-600 text-white w-full"
                  >
                    <LogOut className="mr-1 h-4 w-4" />
                    Déconnexion
                  </Button>
                </SheetClose>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
});

export default MobileNavigation;
